/**
 * FkSelectEditor - Simple FK select for direct FK fields
 *
 * Shows a dropdown of PK values from the referenced table.
 * Used for FK fields with human-readable PKs (like currency "USD", country "SE").
 */

import { useState, useEffect, useCallback } from "react";
import { Select, MenuItem, CircularProgress, FormControl } from "@mui/material";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../../providers/WorkspaceProvider";

interface FkSelectEditorProps extends GridRenderEditCellParams {
  fkReferenceTableId?: string;
  nullable?: boolean;
}

export default function FkSelectEditor({
  id,
  value,
  field,
  fkReferenceTableId,
  nullable,
}: FkSelectEditorProps) {
  const apiRef = useGridApiContext();
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  // Fetch PK values from the referenced table
  useEffect(() => {
    async function fetchOptions() {
      if (!fkReferenceTableId) {
        setLoading(false);
        return;
      }

      try {
        const response = await authFetch(
          `/api/workspace/myvault/schemas/tables/${fkReferenceTableId}/pk-values`,
          "fetch FK PK values",
          { headers: { "x-workspace-id": workspaceId } },
        );
        setOptions(response || []);
      } catch (error) {
        console.error("Error fetching FK options:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchOptions();
  }, [fkReferenceTableId, authFetch, workspaceId]);

  const handleChange = useCallback(
    (event: { target: { value: string } }) => {
      const finalValue = event.target.value || null;
      apiRef.current.setEditCellValue({ id, field, value: finalValue });
      // Close edit mode after selection
      setTimeout(() => {
        apiRef.current.stopCellEditMode({ id, field });
      }, 0);
    },
    [apiRef, id, field],
  );

  if (loading) {
    return (
      <FormControl
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={20} />
      </FormControl>
    );
  }

  return (
    <Select
      value={(value as string) || ""}
      onChange={handleChange}
      autoFocus
      open
      size="small"
      variant="outlined"
      fullWidth
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 300 },
        },
      }}
    >
      {nullable && (
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
}
